import React from 'react';
import LegalDocumentLayout from './LegalDocumentLayout';

const DataProcessingAgreement = () => (
  <LegalDocumentLayout title="Data Processing Agreement" subtitle="Version 5.0 · Effective upon acceptance · Governed by the laws of the State of Delaware, USA">
    <p>
      This Data Processing Agreement ("DPA") is a schedule to the Axaris Terms of Use ("ToS"), which is the
      master agreement. In any conflict, the ToS governs commercial, financial, and liability matters; this DPA
      governs personal data processing obligations. This DPA is entered into between Cloud Guidant Inc., a
      Delaware corporation ("Cloud Guidant"), and the Customer accepting the ToS. Acceptance of the ToS
      constitutes simultaneous acceptance of this DPA. As the Services are a live and evolving platform, the data
      processing activities described herein may expand, change, or be supplemented at any time as new features
      and services are introduced; Section 5 (Permitted Processing) is intentionally broad to accommodate all
      current and future processing activities.
    </p>

    <h2>1. Definitions</h2>
    <p>
      <strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural
      person processed by Cloud Guidant in the course of providing the Services in their current or any future
      form, including data submitted directly, ingested via Connectors, contained in Uploaded Content, or
      generated through any current or future feature of the Services.
    </p>
    <p>
      <strong>"Services"</strong> has the meaning given in the ToS and includes all current and future modules,
      features, connectors, integrations, AI capabilities, and data processing activities as the platform evolves
      over time.
    </p>
    <p>
      <strong>"Connector Data"</strong> means data retrieved from Third-Party Platforms via any current or future
      API integration authorised by the Customer, including without limitation Microsoft Azure, Amazon Web
      Services, Wiz, ServiceNow, Jira, and any other platforms connected from time to time.
    </p>
    <p>
      <strong>"Uploaded Content"</strong> means any documents, files, questionnaire responses, or materials
      uploaded directly by the Customer, including for AI assessments via any current or future assessment feature.
    </p>
    <p>
      <strong>"Dark Web Intelligence Data"</strong> means data from open, dark web, and third-party threat
      intelligence sources processed in connection with any current or future threat monitoring feature of the
      Services.
    </p>
    <p>
      <strong>"Workforce Intelligence Data"</strong> means capacity, FTE, skill, and financial data processed in
      connection with any current or future workforce analysis feature.
    </p>
    <p>
      <strong>"Derived Data"</strong> means aggregated, anonymised, or de-identified data derived from any
      Personal Data category. Derived Data is Cloud Guidant's exclusive property, is not Personal Data, and is not
      subject to this Agreement.
    </p>

    <h2>2. Living Platform and Evolving Services</h2>
    <p>
      Axaris is a live, continuously evolving platform. Cloud Guidant adds, modifies, replaces, deprecates, and
      removes features, modules, connectors, AI models, scoring methodologies, data sources, integrations, pricing
      structures, and all other aspects of the Services at any time, at its sole discretion, without advance
      notice, and without any obligation to notify the Customer.
    </p>
    <p className="legal-caps">
      NO FEATURE, MODULE, CONNECTOR, INTEGRATION, DATA SOURCE, AI MODEL, SCORING METHODOLOGY, PRICING TIER, OR
      ANY OTHER ASPECT OF THE SERVICES CONSTITUTES A PERMANENT, COMMITTED, OR GUARANTEED OFFERING. THE CUSTOMER
      HAS NO RIGHT TO EXPECT ANY SPECIFIC FEATURE, CAPABILITY, OR FUNCTIONALITY TO REMAIN AVAILABLE IN ANY FORM.
    </p>
    <p>The following do not constitute contractual commitments and create no enforceable obligations on Cloud Guidant of any kind:</p>
    <ul>
      <li>Product documentation, user guides, help articles, and knowledge base content, which may be updated or removed at any time without notice</li>
      <li>Marketing materials, pitch decks, product overviews, website content, and sales presentations, regardless of when provided or what they describe</li>
      <li>Features demonstrated in product demos, trials, proof-of-concept engagements, or beta access</li>
      <li>Roadmap items, feature requests acknowledged by Cloud Guidant, or verbal or written statements about future functionality</li>
      <li>Features available in any prior version of the Services or any prior subscription period</li>
      <li>AI model behaviour, output quality, scoring methodology, or analytical framework, all of which may change as models are updated or retrained</li>
    </ul>
    <p>Cloud Guidant may at any time:</p>
    <ul>
      <li>Add new modules, connectors, or capabilities (which automatically become part of the Services and subject to these Terms without further notice)</li>
      <li>Modify or deprecate existing modules, connectors, or capabilities</li>
      <li>Change the pricing of any feature, tier, or add-on</li>
      <li>Change the connector list, which is live and continuously updated</li>
      <li>Retrain or replace AI models, which may produce different outputs for identical inputs</li>
      <li>Change data residency region availability</li>
    </ul>
    <p>
      Continued use of the Services following any change constitutes the Customer's full and unconditional
      acceptance of the Services as modified. Cloud Guidant shall have no liability of any kind for any loss,
      disruption, or inconvenience arising from any change to the Services, regardless of its nature or impact on
      the Customer's operations or reliance on any prior feature or capability.
    </p>

    <h2>3. Roles of the Parties</h2>
    <p>
      The Customer is the Data Controller and bears sole responsibility for the lawfulness of all Personal Data
      submitted to or ingested by the Services in any form, including via current and future Connectors and
      Uploaded Content. Cloud Guidant acts as Data Processor. Cloud Guidant bears no liability for processing
      resulting from Customer's acts, omissions, or instructions.
    </p>
    <p>
      Third-Party Platforms accessed via Connectors are independent controllers or processors in their own right,
      operating under their own terms. They are not sub-processors of Cloud Guidant. The Customer is solely
      responsible for compliance with each such platform's requirements. This applies to all current and any
      future Third-Party Platforms connected via any current or future Connector.
    </p>

    <h2>4. Scope of Personal Data Processed</h2>
    <p>
      Cloud Guidant processes Personal Data across the following categories, which may expand as the Services
      evolve and new features are introduced. The Customer accepts that new data processing activities may
      commence without further notice as part of new or updated features:
    </p>
    <ul>
      <li><strong>Account and identity data:</strong> names, email addresses, and authentication data stored in a globally-hosted US identity layer for platform access via Microsoft Entra authentication</li>
      <li><strong>Risk intelligence data:</strong> security findings, scores, and assessment outputs, stored in the Customer's designated data residency region</li>
      <li><strong>Connector Data:</strong> configuration data, access logs, and resource metadata from connected Third-Party Platforms, including all future Connectors as made available</li>
      <li><strong>Uploaded Content:</strong> documents, questionnaire responses, and materials uploaded for any current or future AI assessment or analysis feature</li>
      <li><strong>Dark Web Intelligence Data:</strong> credential exposure and threat intelligence from third-party sources, processed for any current or future threat monitoring feature</li>
      <li><strong>Workforce Intelligence Data:</strong> FTE, skill, and financial data for any current or future workforce analysis feature</li>
      <li>Any additional categories of Personal Data processed in connection with future features, modules, or services introduced by Cloud Guidant from time to time</li>
    </ul>

    <h2>5. Permitted Processing</h2>
    <p>
      Cloud Guidant may process Personal Data for any purpose necessary to provide the Services in their current
      or any future form. This includes without limitation: all current modules and any future modules, features,
      or services introduced at any time; AI-powered risk analysis, assessment, observation, remediation guidance,
      GRC readiness evaluation, financial impact quantification, dark web monitoring, workforce intelligence, and
      any future AI capabilities; AI model training, retraining, and improvement using Derived Data; generating
      Derived Data for any commercial purpose; and complying with applicable law. The scope of permitted
      processing automatically expands as new features and services are added to the platform without any
      requirement to update this Agreement.
    </p>
    <p>
      Cloud Guidant may freely use Derived Data for any business purpose without restriction or notice. Dark Web
      Intelligence Data from third-party providers is processed solely to deliver outputs; Cloud Guidant makes no
      warranty as to its accuracy and shall not be liable for claims arising from its content.
    </p>

    <h2>6. Customer Responsibilities</h2>
    <p>
      The Customer is solely responsible for: the legality of all Personal Data submitted or ingested, including
      via any future Connector or feature; obtaining all required consents; the scope of access granted via
      Connector credentials; ensuring questionnaire responses and Uploaded Content do not contain unnecessary
      Personal Data; revoking Connector credentials promptly on termination; and compliance with applicable law.
      Cloud Guidant bears no liability for any failure by the Customer to meet these responsibilities, including
      in relation to future features or Connectors the Customer enables.
    </p>

    <h2>7. Sub-processors</h2>
    <p>Cloud Guidant engages the following categories of sub-processor, which may be updated at any time without notice:</p>
    <ul>
      <li><strong>Microsoft Azure</strong> — cloud infrastructure, compute, storage, AI inference, and identity services</li>
      <li><strong>Proprietary dark web and threat intelligence partners</strong> — identities withheld for IP reasons; contractually bound to equivalent data protection standards</li>
      <li>Any additional sub-processors engaged in connection with future features or services, for which blanket irrevocable advance authorisation is hereby granted</li>
    </ul>
    <p>
      The Customer provides blanket, irrevocable authorisation for Cloud Guidant to engage, replace, or modify
      sub-processors at any time without notice, including in connection with new features and services.
      Third-Party Platforms accessed via Connectors (all current and future) are not sub-processors of Cloud
      Guidant and operate under their own terms.
    </p>

    <h2>8. Data Residency and Architecture</h2>
    <p>
      Risk intelligence data and AI processing are region-aligned where available, with dedicated in-region AI
      deployment options available for Customers with strict jurisdictional requirements. Unified reporting uses
      query-time aggregation only; no risk data is persisted outside its origin region. Account and identity data
      is in a US-hosted global identity layer. Schema-level tenant isolation applies. Cloud Guidant makes no
      warranty that AI processing will be confined to any specific region and shall not be liable for any
      regulatory consequence arising from AI processing location. Cloud Guidant may add, modify, or deprecate
      regional availability at any time without notice. The Customer's nominated region may change if a region is
      deprecated; Cloud Guidant will use reasonable efforts to migrate data but shall not be liable for any
      inconvenience arising from regional changes.
    </p>

    <h2>9. Security</h2>
    <p>
      Cloud Guidant implements security measures it deems appropriate at its discretion, including AES-256 at
      rest, TLS 1.2+ in transit, schema-level tenant isolation, RBAC, and Entra authentication. Security measures
      change as the platform evolves without notice. Cloud Guidant does not warrant all incidents can be prevented
      and is not liable for incidents not caused by its gross negligence.
    </p>

    <h2>10. No Audit or Inspection Rights</h2>
    <p>
      The Customer expressly and irrevocably waives all rights to audit, inspect, or verify Cloud Guidant's
      systems, facilities, personnel, records, or operations in any form, at any time, for any reason. By
      accepting this Agreement, the Customer confirms it has completed its own due diligence, is satisfied with
      Cloud Guidant's practices on the basis of this Agreement alone, and acknowledges that no certifications,
      audit reports, or compliance evidence of any kind will be provided now or at any future time, to the fullest
      extent permitted by law.
    </p>

    <h2>11. Data Breach Notification</h2>
    <p>
      For confirmed breaches directly attributable to Cloud Guidant's own systems, Cloud Guidant will make
      commercially reasonable efforts to notify the Customer within a commercially reasonable timeframe.
      Notification is not an admission of liability. Cloud Guidant has no obligation to notify regulators or data
      subjects on the Customer's behalf unless expressly required by law. Cloud Guidant is not liable for breaches
      caused by Third-Party Platforms, Connector misconfigurations, or Customer actions.
    </p>

    <h2>12. Data Subject Rights</h2>
    <p>
      The Customer is solely responsible for responding to data subject rights requests. Cloud Guidant provides
      reasonable technical assistance within current Service capabilities, at its discretion, and may charge fees
      beyond that scope. Cloud Guidant bears no liability for Customer failures. For Dark Web Intelligence Data,
      Cloud Guidant cannot guarantee correction or erasure as data originates from external sources. Future
      features may introduce new data processing activities; data subject rights obligations for such activities
      are the Customer's responsibility as Data Controller.
    </p>

    <h2>13. International Data Transfers</h2>
    <p>
      Risk intelligence data is confined to the Customer's designated region. Account and identity data is
      processed in the United States. By accepting this Agreement, the Customer expressly consents to US
      processing for identity and authentication purposes.
    </p>
    <p>
      Where the Customer is located in the European Economic Area or United Kingdom and applicable law requires a
      lawful transfer mechanism for the transfer of Personal Data to the United States, Cloud Guidant will, upon
      the Customer's written request, execute the European Commission's Standard Contractual Clauses (Module 2:
      Controller to Processor) or the UK International Data Transfer Addendum as applicable ("SCCs"). The
      Customer is solely responsible for: (a) determining whether SCCs or any other transfer mechanism are
      required in its jurisdiction; (b) requesting execution of SCCs before submitting any Personal Data subject
      to such requirements; and (c) compliance with all transfer obligations applicable to it as Data Controller.
    </p>
    <p>
      Cloud Guidant makes no warranty as to the legal sufficiency of any transfer mechanism under any specific
      jurisdiction and shall not be liable for any regulatory action arising from cross-border transfer
      requirements. The Customer's failure to request SCCs prior to submitting Personal Data does not create any
      obligation on Cloud Guidant to execute them retroactively or to cease processing data already submitted.
    </p>

    <h2>14. Liability and Remedies</h2>
    <p>
      All liability, limitation of liability, damages, refunds, and remedies are governed exclusively by the ToS.
      Nothing in this DPA creates additional liability for Cloud Guidant. This includes liability for any data
      processing activity introduced in connection with future features or services.
    </p>

    <h2>15. Indemnification</h2>
    <p>
      The Customer shall defend, indemnify, and hold harmless Cloud Guidant and all Indemnified Parties (as
      defined in the ToS) from all claims arising from: Customer's breach of this DPA or applicable law; Personal
      Data or Uploaded Content submitted by the Customer; Connector configurations and access scope; any claim by
      a data subject or regulator attributable to Customer; and the Customer's use of any current or future
      feature of the Services. This obligation survives termination.
    </p>

    <h2>16. Term, Termination, and Data Retention</h2>
    <p>
      This DPA is coterminous with the ToS. Upon termination, Personal Data is retained for up to 180 days then
      deleted per Cloud Guidant's standard schedule. Derived Data is retained indefinitely. Data export requests
      are subject to fees and Cloud Guidant's discretion. Cloud Guidant is not liable for data not recoverable
      after deletion. Retention schedules may change as the platform evolves.
    </p>

    <h2>17. Amendments and Living Platform</h2>
    <p>
      Cloud Guidant may amend this DPA at any time, including to reflect new data processing activities introduced
      by new features and services, by posting a revised version within the platform. No notice is required.
      Continued use constitutes acceptance. The Customer's sole remedy for disagreement is to terminate its
      account. This DPA is intentionally drafted broadly to accommodate the full scope of current and future data
      processing activities on the Axaris platform without requiring amendment each time new features are
      introduced.
    </p>

    <h2>18. Governing Law and Arbitration</h2>
    <p>
      This DPA is governed by Delaware law. All disputes resolved by binding individual AAA arbitration in
      Wilmington, Delaware.
    </p>
    <p className="legal-caps">
      THE CUSTOMER WAIVES ALL CLASS, COLLECTIVE, OR REPRESENTATIVE PROCEEDINGS. CLAIMS NOT SUBMITTED WITHIN ONE
      (1) YEAR ARE PERMANENTLY BARRED.
    </p>
    <p>Non-assignable by Customer; freely assignable by Cloud Guidant.</p>

    <h2>19. Miscellaneous</h2>
    <p>
      If any provision is invalid, it is modified to the minimum extent necessary. Remaining provisions continue
      in force. This DPA together with the ToS constitutes the entire agreement on data processing. Cloud Guidant
      is not liable for force majeure events.
    </p>
  </LegalDocumentLayout>
);

export default DataProcessingAgreement;
